import React, { useState, useRef, useEffect } from 'react';
import { View, Image, StyleSheet, Dimensions, TouchableOpacity, Linking, Platform } from 'react-native';
import CarouselContainer from '../../../Common/CarouselContainer';
import PlatformDetector from '../../../../utils/PlatformDetector';

// Aspect ratio de los banners (ancho:alto)
const BANNER_ASPECT_RATIO = 2.2;

/**
 * Componente reutilizable para mostrar un carrusel de banners
 * Se adapta proporcionalmente al tamaño de la pantalla
 */
const BannerCarousel = ({ banners = [], loading = false, position = 'left', isMobileView = false }) => {
  const scrollViewRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [containerWidth, setContainerWidth] = useState(0);

  // Calcular dimensiones responsivas basadas en el contenedor
  const calculateDimensions = (availableWidth, fallbackViewportWidth) => {
    const viewportWidth = fallbackViewportWidth || Dimensions.get('window').width;
    let bannerWidth = availableWidth;

    if (!bannerWidth || bannerWidth <= 0) {
      bannerWidth = isMobileView ? viewportWidth * 0.92 : viewportWidth * 0.47;
    }

    if (isMobileView) {
      // Evitar que el banner sea más ancho que la vista del móvil
      bannerWidth = Math.min(bannerWidth, viewportWidth * 0.92);
    }

    const height = bannerWidth / BANNER_ASPECT_RATIO;
    return { width: bannerWidth, height };
  };

  // Efecto para calcular dimensiones iniciales y manejar resize
  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = PlatformDetector.isWeb()
        ? window.innerWidth
        : Dimensions.get('window').width;
      setDimensions(calculateDimensions(containerWidth, viewportWidth));
    };

    handleResize();

    if (PlatformDetector.isWeb()) {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }

    const subscription = Dimensions.addEventListener('change', handleResize);
    return () => subscription?.remove?.();
  }, [containerWidth, isMobileView]);

  // Filtrar banners válidos
  const validBanners = banners.filter(banner => !!banner.imagenWeb).map(banner => ({
    id: banner.codBanner.toString(),
    image: banner.imagenWeb,
    link: banner.link,
    titulo: banner.titulo
  }));

  // Agregar banners fantasma para efecto infinito
  const carouselBanners = validBanners.length > 1 ? [
    { ...validBanners[validBanners.length - 1], id: `${position}-last` },
    ...validBanners,
    { ...validBanners[0], id: `${position}-first` },
  ] : validBanners;

  // Inicialización del carrusel
  useEffect(() => {
    if (validBanners.length > 1 && dimensions.width > 0) {
      scrollViewRef.current?.scrollTo({
        x: dimensions.width,
        animated: false
      });
      setCurrentPage(1);
    }
  }, [validBanners.length, dimensions.width]);

  // Auto-scroll
  useEffect(() => {
    if (validBanners.length > 1 && dimensions.width > 0) {
      const interval = setInterval(() => {
        let nextPage = currentPage + 1;
        if (nextPage >= carouselBanners.length) {
          nextPage = 0;
        }
        scrollViewRef.current?.scrollTo({
          x: nextPage * dimensions.width,
          animated: true
        });
        setCurrentPage(nextPage);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [validBanners.length, currentPage, dimensions.width]);

  // Manejador de scroll
  const handleScroll = (event) => {
    if (validBanners.length <= 1 || dimensions.width === 0) return;

    const contentOffset = event.nativeEvent.contentOffset;
    const page = Math.round(contentOffset.x / dimensions.width);
    setCurrentPage(page);

    // Salto infinito
    if (page === 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({
          x: (carouselBanners.length - 2) * dimensions.width,
          animated: false
        });
        setCurrentPage(carouselBanners.length - 2);
      }, 0);
    } else if (page === carouselBanners.length - 1) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({
          x: dimensions.width,
          animated: false
        });
        setCurrentPage(1);
      }, 0);
    }
  };

  // Renderizar banner individual
  const renderBanner = (banner, index) => {
    // Obtener el banner original para acceder al campo apertura
    const originalBanner = banners.find(b => b.codBanner.toString() === banner.id);
    const abrirEnPestañaNueva = originalBanner?.apertura === 1;
    
    return (
    <TouchableOpacity
      key={banner.id}
      style={[styles.bannerContainer, { width: dimensions.width, height: dimensions.height }]}
      onPress={() => {
        if (!banner.id.includes('first') && !banner.id.includes('last')) {
          const isDesktop = dimensions.width >= 600;
          
          // Si el banner tiene un link definido, redirigir a ese link
          if (banner.link && banner.link.trim() !== '') {
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              // Extraer solo la parte de la URL después del dominio
              const linkUrl = banner.link.includes('pinguino.com.ar') 
                ? banner.link.split('pinguino.com.ar')[1] 
                : banner.link;
              
              // Determinar si es URL externa
              const isExternalUrl = banner.link.startsWith('http://') || banner.link.startsWith('https://');
              const finalUrl = isExternalUrl ? banner.link : (window.location.origin + linkUrl);
              
              // Si apertura es 0, abrir en pestaña nueva (solo en desktop)
              if (abrirEnPestañaNueva && isDesktop) {
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
                return;
              }
              
              // Si apertura es 1 o estamos en móvil, navegar en la misma pestaña
              if (isExternalUrl) {
                window.location.href = finalUrl;
              } else {
                const isOnProductsPage = window.location.pathname === '/productos';
                
                if (isOnProductsPage) {
                  window.history.pushState({}, '', linkUrl);
                  window.dispatchEvent(new Event('navigationChange'));
                } else {
                  window.location.href = finalUrl;
                }
              }
            } else {
              Linking.openURL(banner.link);
            }
            return;
          }

          // Si no hay link, usar la lógica actual de navegación
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const urlParams = new URLSearchParams();
            urlParams.append('D', 0);
            urlParams.append('C', 0);
            urlParams.append('S', 0);
            urlParams.append('codBanner', banner.id);
            urlParams.append('searchText', '');
            const url = `/productos?${urlParams.toString()}`;
            
            // Si apertura es 0, abrir en pestaña nueva (solo en desktop)
            if (abrirEnPestañaNueva && isDesktop) {
              const fullUrl = window.location.origin + url;
              window.open(fullUrl, '_blank', 'noopener,noreferrer');
              return;
            }
            
            // Si apertura es 1 o estamos en móvil, navegar en la misma pestaña
            const isOnProductsPage = window.location.pathname === '/productos';
            
            if (isOnProductsPage) {
              window.history.pushState({}, '', url);
              window.dispatchEvent(new Event('navigationChange'));
            } else {
              window.location.href = url;
            }
          }
        }
      }}
      activeOpacity={0.8}
    >
      <Image
        source={{ uri: banner.image }}
        style={styles.bannerImage}
        resizeMode="contain"
      />
    </TouchableOpacity>
    );
  };

  // No renderizar si no hay banners y no está cargando
  if (validBanners.length === 0 && !loading) {
    return null;
  }

  // Calcular índice real para paginación
  const realIndex = currentPage - 1 < 0 ? validBanners.length - 1 : currentPage - 1;

  return (
    <View
      style={[styles.wrapper, { height: dimensions.height + 30 }]}
      onLayout={(event) => {
        const width = event.nativeEvent.layout.width;
        if (Math.abs(width - containerWidth) > 1) {
          setContainerWidth(width);
        }
      }}
    >
      <CarouselContainer
        ref={scrollViewRef}
        horizontal
        pagingEnabled
        snapToInterval={dimensions.width}
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        style={styles.carousel}
        contentContainerStyle={styles.carouselContent}
      >
        {carouselBanners.map((banner, index) => renderBanner(banner, index))}
      </CarouselContainer>

      {validBanners.length > 1 && (
        <View style={styles.pagination}>
          {validBanners.map((_, index) => (
            <View
              key={index}
              style={[
                styles.dot,
                { backgroundColor: index === realIndex ? '#ff0000' : 'rgba(0,0,0,0.3)' }
              ]}
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    minWidth: 280,
    alignItems: 'center',
    justifyContent: 'center',
  },
  carousel: {
    width: '100%',
  },
  carouselContent: {
    alignItems: 'center',
  },
  bannerContainer: {
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }
      : {
          elevation: 3,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.2,
          shadowRadius: 4,
        }
    ),
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' ? { objectFit: 'contain' } : {}),
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
    width: '100%',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginHorizontal: 4,
  },
});

export default BannerCarousel;
